import React, { useState, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import enhancedApiClient from '../api/enhancedApiClient';

const GuestCard = ({ guest, id }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    padding: '10px 14px',
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #E5E7EB',
    cursor: 'grab',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '14px',
    marginBottom: '6px',
    boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : 'none'
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div style={{
        width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#E8F4E8',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '11px', fontWeight: '600', color: '#6B9080', flexShrink: 0
      }}>
        {guest.name?.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: '500', color: '#1F2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {guest.name}
        </div>
        {guest.rsvpStatus && (
          <div style={{ fontSize: '11px', color: guest.rsvpStatus === 'confirmed' ? '#059669' : '#9CA3AF', marginTop: '2px' }}>
            {guest.rsvpStatus}
          </div>
        )}
      </div>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="2">
        <line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="18" x2="16" y2="18"/>
      </svg>
    </div>
  );
};

const DroppableGroup = ({ id, title, guests, onRename, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(title);

  const { setNodeRef } = useSortable({ id, data: { type: 'group' } });

  return (
    <div ref={setNodeRef} style={{
      backgroundColor: '#F9FAFB',
      borderRadius: '12px',
      padding: '16px',
      minWidth: '240px',
      maxWidth: '280px',
      width: '260px',
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '100%',
      border: '1px solid #E5E7EB'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        {isEditing ? (
          <input
            type="text" value={editName} onChange={(e) => setEditName(e.target.value)}
            onBlur={() => { onRename(id, editName); setIsEditing(false); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { onRename(id, editName); setIsEditing(false); } }}
            autoFocus
            style={{ padding: '4px 8px', border: '1px solid #6B9080', borderRadius: '4px', fontSize: '14px', fontWeight: '600', width: '100%' }}
          />
        ) : (
          <h4 onClick={() => id !== 'unassigned' && setIsEditing(true)}
            style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: 0, cursor: id !== 'unassigned' ? 'pointer' : 'default' }}>
            {title} ({guests.length})
          </h4>
        )}
        {id !== 'unassigned' && !isEditing && (
          <button onClick={() => onDelete(id)}
            style={{ background: 'none', border: 'none', color: '#D1D5DB', cursor: 'pointer', padding: '2px' }}
            onMouseOver={(e) => e.currentTarget.style.color = '#DC2626'}
            onMouseOut={(e) => e.currentTarget.style.color = '#D1D5DB'}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        )}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', minHeight: '80px' }}>
        <SortableContext items={guests.map(g => g.id)} strategy={verticalListSortingStrategy}>
          {guests.map(guest => (
            <GuestCard key={guest.id} id={guest.id} guest={guest} />
          ))}
        </SortableContext>
        {guests.length === 0 && (
          <div style={{ padding: '24px 12px', textAlign: 'center', color: '#D1D5DB', fontSize: '13px', border: '2px dashed #E5E7EB', borderRadius: '8px' }}>
            Drop guests here
          </div>
        )}
      </div>
    </div>
  );
};

const GuestGroupsView = ({ guests, eventId, onUpdate }) => {
  const [groupNames, setGroupNames] = useState(['unassigned']);
  const [guestGroups, setGuestGroups] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');

  // Initialize groups from guests data + load saved group names from event
  useEffect(() => {
    const init = async () => {
      // Build groups from guest group field
      const groupMap = { unassigned: [] };
      for (const guest of guests) {
        const group = guest.group || 'unassigned';
        if (!groupMap[group]) groupMap[group] = [];
        groupMap[group].push(guest);
      }

      // Load saved group names from event
      try {
        const eventData = await enhancedApiClient.makeRequest(`/events/${eventId}`);
        const savedGroups = eventData?.guestGroups || [];
        for (const g of savedGroups) {
          if (!groupMap[g]) groupMap[g] = [];
        }
      } catch {}

      const allGroups = ['unassigned', ...Object.keys(groupMap).filter(k => k !== 'unassigned').sort()];
      setGroupNames(allGroups);
      setGuestGroups(groupMap);
    };
    init();
  }, [guests, eventId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const findGroup = (id) => {
    for (const [groupId, groupGuests] of Object.entries(guestGroups)) {
      if (groupGuests.some(g => g.id === id)) return groupId;
    }
    return null;
  };

  const saveGuestGroup = async (guestId, groupId) => {
    try {
      await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/guests/${eventId}/${guestId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group: groupId === 'unassigned' ? '' : groupId })
      });
      if (onUpdate) onUpdate(guestId, groupId);
    } catch (e) {
      console.error('Failed to save guest group:', e);
    }
  };

  const saveGroupNames = async (names) => {
    try {
      await enhancedApiClient.makeRequest(`/events/${eventId}`, {
        method: 'PUT',
        body: JSON.stringify({ guestGroups: names.filter(n => n !== 'unassigned') })
      });
    } catch (e) {
      console.error('Failed to save group names:', e);
    }
  };

  const handleDragStart = (event) => setActiveId(event.active.id);

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeGroup = findGroup(active.id);
    let overGroup = findGroup(over.id);

    // If over is a group container
    if (!overGroup && guestGroups[over.id] !== undefined) overGroup = over.id;
    if (!activeGroup || !overGroup || activeGroup === overGroup) return;

    setGuestGroups(prev => {
      const guest = prev[activeGroup].find(g => g.id === active.id);
      if (!guest) return prev;
      return {
        ...prev,
        [activeGroup]: prev[activeGroup].filter(g => g.id !== active.id),
        [overGroup]: [...(prev[overGroup] || []), guest]
      };
    });
  };

  const handleDragEnd = async (event) => {
    const { active } = event;
    setActiveId(null);
    const groupId = findGroup(active.id);
    if (groupId) {
      await saveGuestGroup(active.id, groupId);
    }
  };

  const addGroup = async () => {
    if (!newGroupName.trim()) return;
    const groupId = newGroupName.trim().toLowerCase().replace(/\s+/g, '-');
    if (guestGroups[groupId]) return;

    const updatedNames = [...groupNames, groupId];
    setGroupNames(updatedNames);
    setGuestGroups(prev => ({ ...prev, [groupId]: [] }));
    setNewGroupName('');
    await saveGroupNames(updatedNames);
  };

  const renameGroup = async (oldId, newName) => {
    if (!newName.trim() || oldId === 'unassigned') return;
    const newId = newName.trim().toLowerCase().replace(/\s+/g, '-');
    if (newId === oldId) return;

    setGuestGroups(prev => {
      const updated = { ...prev };
      updated[newId] = (updated[oldId] || []).map(g => ({ ...g, group: newId }));
      delete updated[oldId];
      return updated;
    });

    const updatedNames = groupNames.map(n => n === oldId ? newId : n);
    setGroupNames(updatedNames);
    await saveGroupNames(updatedNames);

    // Update all guests in this group
    for (const guest of guestGroups[oldId] || []) {
      await saveGuestGroup(guest.id, newId);
    }
  };

  const deleteGroup = async (groupId) => {
    if (groupId === 'unassigned') return;
    const guestsToMove = guestGroups[groupId] || [];

    setGuestGroups(prev => {
      const updated = { ...prev };
      updated.unassigned = [...updated.unassigned, ...guestsToMove];
      delete updated[groupId];
      return updated;
    });

    const updatedNames = groupNames.filter(n => n !== groupId);
    setGroupNames(updatedNames);
    await saveGroupNames(updatedNames);

    for (const guest of guestsToMove) {
      await saveGuestGroup(guest.id, 'unassigned');
    }
  };

  const activeGuest = activeId ? Object.values(guestGroups).flat().find(g => g.id === activeId) : null;

  const getDisplayName = (id) => {
    if (id === 'unassigned') return 'Unassigned';
    return id.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <input
          type="text" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addGroup()}
          placeholder="New group name (e.g. Table 1, Bridal Party)"
          style={{ flex: 1, padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px' }}
        />
        <button onClick={addGroup}
          style={{ padding: '10px 20px', backgroundColor: '#6B9080', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          + Add Group
        </button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div style={{ display: 'flex', gap: '16px', overflowX: 'auto', paddingBottom: '16px', minHeight: '400px', alignItems: 'flex-start' }}>
          {groupNames.map(groupId => (
            <DroppableGroup
              key={groupId}
              id={groupId}
              title={getDisplayName(groupId)}
              guests={guestGroups[groupId] || []}
              onRename={renameGroup}
              onDelete={deleteGroup}
            />
          ))}
        </div>

        <DragOverlay>
          {activeGuest ? (
            <div style={{
              padding: '10px 14px', backgroundColor: 'white', borderRadius: '8px',
              border: '2px solid #6B9080', boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', width: '240px'
            }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#E8F4E8',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', fontWeight: '600', color: '#6B9080'
              }}>
                {activeGuest.name?.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()}
              </div>
              <span style={{ fontWeight: '500', color: '#1F2937' }}>{activeGuest.name}</span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default GuestGroupsView;
